<template>
  <div class="local-player">
    <div class="local-player__frame">
      <video
        ref="videoEl"
        class="local-player__video"
        :src="videoUrl"
        playsinline
        @loadedmetadata="onLoadedMetadata"
        @timeupdate="onTimeUpdate"
        @play="onPlay"
        @pause="onPause"
        @ended="onPause"
      ></video>
      <!-- Timer overlay removed (progress shown below) -->
    </div>

    <div class="local-player__meta">
      <span class="local-player__meta-item">{{ safeTime(currentTime) }}</span>
      <span class="local-player__meta-sep">/</span>
      <span class="local-player__meta-item">{{ safeTime(duration) }}</span>
    </div>

    <div class="controls mt-2">
      <div class="player-controls__zone player-controls__zone--start">
        <button @click="toggleSpeed" class="btn btn-outline-secondary btn-lg text-black" :class="{ active: isSlow }">
          <i v-if="isSlow" class="fas fa-running text-white"></i>
          <i v-else class="fas fa-running"></i>
        </button>
      </div>

      <div class="player-controls__zone player-controls__zone--center">
        <button class="btn btn-secondary btn-lg border border-1 border-black" @click="backwardOneSecond">
          <i class="fa-solid fa-angles-left"></i>
        </button>

        <button class="btn btn-secondary btn-lg border border-1 border-black mx-1" @click="togglePlayPause">
          <i :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
        </button>

        <button class="btn btn-secondary btn-lg border border-1 border-black" @click="forwardOneSecond">
          <i class="fa-solid fa-angles-right"></i>
        </button>
      </div>

      <div class="player-controls__zone player-controls__zone--end">
        <div v-if="!readOnly" class="d-flex align-content-center gap-2">
        <button
          class="btn btn-primary btn-lg d-flex align-items-center justify-content-center"
          :disabled="uploadDisabled"
          @click="confirmVideo"
        >
          <i class="fa-solid fa-check"></i>
        </button>
        <button class="btn btn-danger btn-lg" @click="deleteVideoUrl">
          <i class="fas fa-cancel"></i>
        </button>
        </div>
      </div>
    </div>

    <input
      type="range"
      class="form-range"
      :value="progress"
      @input="seekToTime"
      :min="0"
      :max="duration"
      step="0.1"
      :style="sliderStyle"
    />
    <ConfirmationModal
      ref="confirmationModal"
      :message="modalMessage"
      :showCancel="showCancel"
      :showRuleSelector="false"
      :ruleOptions="[]"
      :showNameInput="false"
      :initialRule="''"
      :initialName="''"
      @confirmed="handleConfirmed"
      @canceled="cancelModal"
    />
  </div>
</template>

<script>
import ConfirmationModal from '@/components/competitions/ConfirmationModal.vue';

export default {
  name: 'LocalVideoPlayer',
  components: { ConfirmationModal },
  emits: ['delete-video-url', 'confirm-video'],
  props: {
    videoUrl: {
      type: String,
      required: true,
    },
    uploadDisabled: {
      type: Boolean,
      default: false,
    },
    uploadBusy: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      duration: 0,
      currentTime: 0,
      isPlaying: false,
      isSlow: false,
      progress: 0,
      remainingSeconds: 0,
      pendingConfirmResolver: null,
      showCancel: false,
      modalMessage: '',
    };
  },
  computed: {
    hasDuration() {
      return Number.isFinite(this.duration) && this.duration > 0;
    },
    sliderStyle() {
      const ratio = this.hasDuration ? Math.min(100, Math.max(0, (this.progress / this.duration) * 100)) : 0;
      return `background: linear-gradient(to right, #4CAF50 0%, #4CAF50 ${ratio}%, #ddd ${ratio}%, #ddd 100%);`;
    },
  },
  watch: {
    videoUrl() {
      this.resetPlayer();
      this.$nextTick(() => {
        this.loadVideo();
      });
    },
  },
  mounted() {
    this.loadVideo();
  },
  beforeUnmount() {
    this.pauseVideo();
  },
  methods: {
    safeTime(value) {
      if (!Number.isFinite(value) || value < 0) return '00:00';
      return this.formatTime(value);
    },
    loadVideo() {
      const video = this.$refs.videoEl;
      if (!video) return;
      video.load();
    },
    onLoadedMetadata() {
      const video = this.$refs.videoEl;
      if (!video) return;
      const dur = Number.isFinite(video.duration) ? video.duration : 0;
      const cur = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      this.duration = dur;
      this.currentTime = cur;
      this.remainingSeconds = Math.floor(Math.max(0, dur - cur));
      this.progress = cur;
    },
    onTimeUpdate() {
      const video = this.$refs.videoEl;
      if (!video) return;
      const cur = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      const dur = Number.isFinite(video.duration) ? video.duration : this.duration;
      this.duration = dur;
      this.currentTime = cur;
      this.progress = cur;
      const remaining = Math.max(0, (dur || 0) - cur);
      this.remainingSeconds = Math.floor(remaining);
    },
    onPlay() {
      this.isPlaying = true;
    },
    onPause() {
      this.isPlaying = false;
    },
    togglePlayPause() {
      const video = this.$refs.videoEl;
      if (!video) return;
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    },
    toggleSpeed() {
      this.isSlow = !this.isSlow;
      const video = this.$refs.videoEl;
      if (!video) return;
      video.playbackRate = this.isSlow ? 0.25 : 1.0;
    },
    seekToTime(event) {
      const video = this.$refs.videoEl;
      if (!video) return;
      const targetTime = parseFloat(event.target.value);
      if (!Number.isFinite(targetTime)) return;
      video.currentTime = targetTime;
      this.onTimeUpdate();
    },
    backwardOneSecond() {
      const video = this.$refs.videoEl;
      if (!video) return;
      video.currentTime = Math.max(0, video.currentTime - 1);
      this.onTimeUpdate();
    },
    forwardOneSecond() {
      const video = this.$refs.videoEl;
      if (!video) return;
      video.currentTime = Math.min(this.duration || 0, video.currentTime + 1);
      this.onTimeUpdate();
    },
    async deleteVideoUrl() {
      const isConfirmed = await this.confirmWithModal('Are you sure you want to remove this local preview?');
      if (isConfirmed) {
        this.$emit('delete-video-url');
      }
    },
    handleConfirmed() {
      if (!this.pendingConfirmResolver) return;
      const resolve = this.pendingConfirmResolver;
      this.resetConfirmationModalState();
      resolve(true);
    },
    cancelModal() {
      if (!this.pendingConfirmResolver) {
        this.resetConfirmationModalState();
        return;
      }
      const resolve = this.pendingConfirmResolver;
      this.resetConfirmationModalState();
      resolve(false);
    },
    resetConfirmationModalState() {
      this.pendingConfirmResolver = null;
      this.showCancel = false;
      this.modalMessage = '';
    },
    confirmWithModal(message) {
      return new Promise((resolve) => {
        this.pendingConfirmResolver = resolve;
        this.showCancel = true;
        this.modalMessage = message;
        this.$refs.confirmationModal.openModal();
      });
    },
    confirmVideo() {
      this.$emit('confirm-video');
    },
    pauseVideo() {
      const video = this.$refs.videoEl;
      if (video) {
        try {
          video.pause();
        } catch (err) {
          // Ignore
        }
      }
    },
    resetPlayer() {
      this.duration = 0;
      this.currentTime = 0;
      this.progress = 0;
      this.remainingSeconds = 0;
      this.isPlaying = false;
      this.isSlow = false;
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      const formattedMinutes = String(minutes).padStart(2, '0');
      const formattedSeconds = String(remainingSeconds).padStart(2, '0');
      return `${formattedMinutes}:${formattedSeconds}`;
    },
  },
};
</script>

<style scoped>
.local-player__frame {
  position: relative;
  width: 100%;
}

.local-player__video {
  width: 100%;
  border-radius: 8px;
  background: #000;
}

.local-player__timer {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  font-family: 'Arial', sans-serif;
  font-size: 20px;
  padding: 2px 8px;
  border-radius: 6px;
}

.local-player__meta {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 14px;
  color: #495057;
  font-variant-numeric: tabular-nums;
}

.local-player__meta-sep {
  color: #adb5bd;
}

.controls {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: center;
  gap: 10px;
}

.player-controls__zone {
  display: flex;
  align-items: center;
  min-width: 0;
}

.player-controls__zone--start {
  justify-content: flex-start;
}

.player-controls__zone--center {
  justify-content: center;
}

.player-controls__zone--end {
  justify-content: flex-end;
}

.controls .btn {
  flex-shrink: 0;
}

input[type="range"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  height: 10px;
  margin: 18px 0;
  border-radius: 5px;
  background: #ddd;
  outline: none;
}

input[type="range"]::-webkit-slider-runnable-track {
  background-color: #ddd;
}

input[type="range"]::-moz-range-track {
  background-color: #ddd;
}

input[type="range"]::-webkit-slider-thumb {
  background-color: #4CAF50;
  border-color: #4CAF50;
}

input[type="range"]::-moz-range-thumb {
  background-color: #4CAF50;
  border-color: #4CAF50;
}

input[type="range"]:focus::-webkit-slider-thumb {
  box-shadow: 0 0 0 1px #ffffff, 0 0 0 0.2rem rgba(76, 175, 80, 0.25);
}

input[type="range"]:focus::-moz-range-thumb {
  box-shadow: 0 0 0 1px #ffffff, 0 0 0 0.2rem rgba(76, 175, 80, 0.25);
}

@media (max-width: 820px) {
  .local-player__meta {
    font-size: 12px;
    margin-top: 4px;
  }

  .controls {
    gap: 6px;
  }

  .controls .btn {
    padding: 0.45rem 0.6rem;
    font-size: 0.95rem;
    line-height: 1.1;
  }

  input[type="range"] {
    margin: 12px 0;
  }
}

@media (max-width: 640px) {
  .controls {
    gap: 4px;
  }

  .controls .btn {
    padding: 0.35rem 0.45rem;
    font-size: 0.82rem;
  }

  .player-controls__zone--center {
    gap: 2px;
  }
}
</style>
